import { z } from 'zod'

import { phoneSchema } from '@/components/ui-custom/PhoneInput'

import { REGEX_PATTERNS } from '../validation/patterns'

// Reusable validations
const nameField = z.string().min(2, 'This field is required').regex(REGEX_PATTERNS.NAME, 'Must contain letters only')

// Optional Arabic (or localized) name fields: allow empty string or >=2 chars matching name pattern
const optionalArabicNameField = z
  .union([
    z.string().trim().min(2, 'Must be at least 2 characters').regex(REGEX_PATTERNS.NAME, 'Must contain letters only'),
    z.literal(''),
  ])
  .optional()
  .nullable()
  .transform((v) => (v === '' ? null : v))

const civilIdField = z.string().min(12, { message: 'Civil ID must be at least 12 characters.' })

const baseFields = {
  first_name: nameField,
  last_name: nameField,
  first_name_ar: optionalArabicNameField,
  last_name_ar: optionalArabicNameField,
  birth_date: z.coerce.date({
    required_error: 'Birth date is required',
  }),
  email: z.string().email({ message: 'Please enter a valid email address' }),
  phone: phoneSchema,
  civil_id: civilIdField,
  country: z.string().min(2, { message: 'Please select country' }),
  city: z
    .string()
    .min(2, { message: 'City is required' })
    .regex(REGEX_PATTERNS.LATIN_ONLY, { message: 'City must contain Latin characters only' }),
}

// Age validation utilities
const isAdult = (date: Date) => {
  const today = new Date()
  const age = today.getFullYear() - date.getFullYear()
  const monthDiff = today.getMonth() - date.getMonth()
  const dayDiff = today.getDate() - date.getDate()
  const beforeBirthday = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)
  return age > 18 || (age === 18 && !beforeBirthday)
}

const isValidBirthDate = (date: Date) => {
  const today = new Date()
  return today.getFullYear() - date.getFullYear() > 1
}

// Base schema shared by all roles
const accountBaseSchema = z.object({
  ...baseFields,
  optin: z.boolean().optional(),
})

// Specific role schemas
export const accountSchema = accountBaseSchema.extend({
  birth_date: baseFields.birth_date.refine(isAdult, {
    message: 'You are under 18. Please contact support to be added.',
  }),
})

export const accountSchemaAdmin = accountBaseSchema.extend({
  birth_date: baseFields.birth_date.refine(isValidBirthDate, {
    message: 'Birth date is required',
  }),
})

export const accountSchemaManager = accountSchemaAdmin.extend({
  branch_id: z.number().nullable().optional(),
})

export const accountSchemaCoach = accountSchemaAdmin.extend({
  branch_id: z.number().nullable().optional(),
})

export const accountSchemaAssistant = accountSchemaAdmin.extend({
  branch_id: z.number().nullable().optional(),
})

// Inferred types
export type accountSchemaValues = z.infer<typeof accountSchema>
export type accountSchemaAdminValues = z.infer<typeof accountSchemaAdmin>
export type accountSchemaManagerValues = z.infer<typeof accountSchemaManager>
export type accountSchemaCoachValues = z.infer<typeof accountSchemaCoach>
export type accountSchemaAssistantValues = z.infer<typeof accountSchemaAssistant>
